import React, { useEffect, useState } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import summaryStopped from "../../../../images/summary_stop.svg";
import { AIMS_STATUS } from "../../../../constants";

import LogManager from "../../../../utils/logManager";
import { THEME_COLOR } from "../../../../styles/colors";

/**
 * Renders an attendee-facing summary-stopped view based on AiMS status.
 * Used when summarizing is stopped and attendees see either no summary or need to contact the organizer.
 *
 * @param {Object} props
 * @param {string} props.aimsStatus - One of AIMS_STATUS.SUMMARY_TO_ORGANIZER | AVAILABLE
 * @param {Object} props.summaryReviewers - Summary reviewer data from API
 */
const AttendeeSummarizeStopped = ({ aimsStatus: initAimsStatus, summaryReviewers }) => {
  const [aimsStatus, setAimsStatus] = useState(initAimsStatus);

  useEffect(() => {
  }, []);

  if (aimsStatus === AIMS_STATUS.AVAILABLE) {
    // Check summary reviewer data to determine what message to show
    if (summaryReviewers && Object.keys(summaryReviewers).length > 0) {
      // Reviewers list is not empty - attendees need to contact organizer/reviewers
      LogManager.info("Reviewers list exists - showing SummaryToReviewersOnly for attendee");
      return <SummaryToReviewersOnly reviewers={summaryReviewers} />;
    }
    
    // Reviewers is empty or no reviewer data - everyone can see (normal message)
    LogManager.info("No reviewers or empty reviewers list - showing normal available message");
    return <StoppedWithSummary />;
  }
  if (aimsStatus === AIMS_STATUS.SUMMARY_TO_ORGANIZER) return <StoppedToOrganizerOnly />;
  return null;
};

const StoppedWithSummary = () => (
  <StoppedTemplate
    image={summaryStopped}
    header="Summarizing meeting stopped"
    message=""
    //TODO: Switch to use the message once this issue fixed: https://sim.amazon.com/issues/MEETEX-169856
    // message="You’ll receive a meeting summary when it’s ready."
  />
);

const SummaryToReviewersOnly = ({ reviewers }) => {
  // Convert reviewers object to a comma-separated string of names
  const reviewerNames = reviewers ? Object.values(reviewers).join(", ") : "";
  
  return (
    <StoppedTemplate
      image={summaryStopped}
      header="Summarizing meeting stopped"
      message={`Contact meeting organizer, ${reviewerNames}, if you want to see the meeting summary.`}
    />
  );
};

const StoppedToOrganizerOnly = () => (
  <StoppedTemplate
    image={summaryStopped}
    header="Summarizing meeting stopped"
    message="Contact meeting organizer if you want to see the meeting summary."
  />
);


const StoppedTemplate = ({ image, header, message }) => (
  <Column alignmentHorizontal="start" width="100%" position="relative" spacing="none" spacingInset="none">
    <Column height="72px" />
    <Row spacing="none" spacingInset="none">
      <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.WHITE }}>
        <Box type="outline" spacingInset="none" minWidth="15px" />
      </Theme>
      <img src={image} alt="" />
    </Row>
    <Column height="16px" spacing="none" />
    <Column spacing="none">
      <Row spacing="none" spacingInset="none">
        <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.WHITE }}>
          <Box type="outline" spacingInset="none" minWidth="68px" />
        </Theme>
        <Text type="h50" alignment="start">
          {header}
        </Text>
      </Row>
    </Column>
    <Column height="16px" spacing="none" />
    <Column spacing="none" spacingInset="none">
      <Row spacing="none" spacingInset="none">
        <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.WHITE }}>
          <Box type="outline" spacingInset="none" minWidth="68px" />
        </Theme>
        <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.WHITE }}>
          <Box type="outline" spacingInset="none" maxWidth="239px">
            <Theme tokens={{ textListSpacingVertical: "160px" }}>
              <Text type="b200" alignment="start" color="secondary">
                {message}
              </Text>
            </Theme>
          </Box>
        </Theme>
      </Row>
    </Column>
  </Column>
);

export default AttendeeSummarizeStopped;
